import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { compressImage, formatCompressionStats } from '../utils/imageCompression';
import { DualPrice } from './DualPrice';
import { CategoryCombobox } from './CategoryCombobox';
import { ImageWithFallback } from './ui/image-with-fallback';
import {
  Plus, Edit, Trash2, Loader2, Package, Image as ImageIcon, Eye, EyeOff, Save
} from 'lucide-react';

/**
 * Drop-in manager: lists a merchant's services and lets staff (Admin or MRO)
 * create / edit / delete / toggle them on the merchant's behalf.
 *
 * Props
 *   api: axios instance (from useAuth)
 *   merchantId: string
 *   urlPrefix: '/api/admin/merchants' | '/api/mro/merchants'
 *   testIdPrefix: e.g. 'admin' | 'mro' for unique data-testids
 */
export function MerchantServicesManager({ api, merchantId, urlPrefix, testIdPrefix = 'staff' }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const emptyForm = {
    name: '', description: '', item_type: 'service', category: '', category_id: '',
    regular_price: '', image_url: '', tags: '', is_active: true,
    tier_prices: {},
  };
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [marketplaceCategories, setMarketplaceCategories] = useState([]);
  const [plans, setPlans] = useState([]);
  const imageInputRef = React.useRef(null);

  // Fetch active subscription plans once so Tier Pricing rows render.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/subscriptions/plans');
        setPlans((data?.plans || []).filter(p => p.is_active !== false));
      } catch (e) { /* non-fatal — tier rows will simply be hidden */ }
    })();
  }, [api]);

  // Refetch the category catalog whenever the item_type toggles, so the
  // dropdown always shows the categories that apply to the current type.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/marketplace/categories?type=${form.item_type || 'service'}`);
        if (!cancelled) setMarketplaceCategories(data.categories || []);
      } catch (e) {
        // Non-fatal — the dropdown will simply be empty.
      }
    })();
    return () => { cancelled = true; };
  }, [api, form.item_type]);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${urlPrefix}/${merchantId}/services`);
      setServices(data.services || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load services');
    }
    setLoading(false);
  }, [api, urlPrefix, merchantId]);

  useEffect(() => { if (merchantId) fetchServices(); }, [merchantId, fetchServices]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview(null);
    setDialogOpen(true);
  };

  const openEdit = (svc) => {
    setEditing(svc);
    setForm({
      name: svc.name || '',
      description: svc.description || '',
      item_type: svc.item_type || 'service',
      category: svc.category || '',
      category_id: svc.category_id || '',
      regular_price: (svc.regular_price ?? '').toString(),
      image_url: svc.image_url || '',
      tags: (svc.tags || []).join(', '),
      is_active: svc.is_active !== false,
      tier_prices: Object.fromEntries(
        Object.entries(svc.tier_prices || {}).map(([k, v]) => [k, (v ?? '').toString()]),
      ),
    });
    setImageFile(null);
    setImagePreview(svc.image_url || null);
    setDialogOpen(true);
  };

  const handleImageSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.category_id) { toast.error('Please pick a category from the list — this powers search and filters.'); return; }
    const rp = parseFloat(form.regular_price);
    if (!rp || rp <= 0) { toast.error('Valid regular price is required'); return; }

    setSaving(true);
    try {
      let imageUrl = form.image_url || null;
      if (imageFile) {
        const before = imageFile.size;
        const compressed = await compressImage(imageFile, { maxSide: 1600, quality: 0.82 });
        if (compressed.size < before) {
          toast.message(`Image compressed: ${formatCompressionStats(before, compressed.size)}`);
        }
        const fd = new FormData();
        fd.append('file', compressed);
        const isProduct = (form.item_type || 'service') === 'product';
        fd.append('purpose', isProduct ? 'product-photo' : 'service-photo');
        if (form.name?.trim()) fd.append('subject_name', form.name.trim());
        const up = await api.post('/api/upload/member-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        imageUrl = up.data.url || up.data.file_url || imageUrl;
      }

      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        item_type: form.item_type || 'service',
        category_id: form.category_id || null,
        category: form.category || 'Other',
        regular_price: rp,
        image_url: imageUrl,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        is_active: !!form.is_active,
        tier_prices: Object.fromEntries(
          Object.entries(form.tier_prices || {})
            .map(([k, v]) => [k, parseFloat(v)])
            .filter(([, v]) => !Number.isNaN(v) && v > 0),
        ),
      };

      if (editing) {
        await api.put(`${urlPrefix}/${merchantId}/services/${editing.id}`, payload);
        toast.success('Service updated');
      } else {
        await api.post(`${urlPrefix}/${merchantId}/services`, payload);
        toast.success('Service created');
      }
      setDialogOpen(false);
      fetchServices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save service');
    }
    setSaving(false);
  };

  const handleToggleActive = async (svc) => {
    try {
      await api.put(`${urlPrefix}/${merchantId}/services/${svc.id}`, { is_active: !svc.is_active });
      toast.success(svc.is_active ? 'Deactivated' : 'Activated');
      fetchServices();
    } catch (e) {
      toast.error('Failed to toggle');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`${urlPrefix}/${merchantId}/services/${confirmDelete.id}`);
      toast.success('Service deleted');
      setConfirmDelete(null);
      fetchServices();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-merchant-services-manager`}>
      <div className="flex items-center justify-between">
        <p className="text-[#5C7599] text-[10px] uppercase tracking-wider font-medium">
          Products / Services ({services.length})
        </p>
        <Button size="sm" onClick={openCreate}
          className="h-7 px-2 text-xs bg-[#00E676] text-[#000B18] hover:bg-[#00E676]/90"
          data-testid={`${testIdPrefix}-add-service-btn`}>
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]" />
        </div>
      ) : services.length === 0 ? (
        <p className="text-center text-[#5C7599] text-xs py-4">No services yet</p>
      ) : (
        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {services.map(svc => (
            <div key={svc.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#000B18] border border-white/5"
              data-testid={`${testIdPrefix}-service-row-${svc.id}`}>
              <div className="w-9 h-9 rounded bg-[#041A3B] flex items-center justify-center flex-shrink-0 overflow-hidden">
                {svc.image_url
                  ? <ImageWithFallback
                      src={svc.image_url}
                      alt={svc.name || 'Service'}
                      className="w-full h-full object-cover"
                      fallback={<ImageIcon className="w-4 h-4 text-[#5C7599]" />}
                    />
                  : <ImageIcon className="w-4 h-4 text-[#5C7599]" />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-xs font-medium truncate">{svc.name}</p>
                <p className="text-[#5C7599] text-[10px]">
                  <DualPrice php={svc.regular_price || 0} phpDecimals={0} usdClassName="opacity-80" />
                  {svc.discount_percentage > 0 && ` · ${svc.discount_percentage}% off`}
                </p>
              </div>
              <button onClick={() => handleToggleActive(svc)}
                className={`p-1.5 rounded ${svc.is_active ? 'text-[#00E676] hover:bg-[#00E676]/10' : 'text-[#5C7599] hover:bg-white/10'}`}
                title={svc.is_active ? 'Deactivate' : 'Activate'}
                data-testid={`${testIdPrefix}-toggle-service-${svc.id}`}>
                {svc.is_active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => openEdit(svc)}
                className="p-1.5 rounded text-[#00E5FF] hover:bg-[#00E5FF]/10"
                title="Edit"
                data-testid={`${testIdPrefix}-edit-service-${svc.id}`}>
                <Edit className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setConfirmDelete(svc)}
                className="p-1.5 rounded text-[#FF1744] hover:bg-[#FF1744]/10"
                title="Delete"
                data-testid={`${testIdPrefix}-delete-service-${svc.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#041A3B] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="w-5 h-5 text-[#00E676]" />
              {editing ? 'Edit Service' : 'New Service'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[#A0B4CC] text-xs">Type</Label>
              <select value={form.item_type} onChange={e => setForm({ ...form, item_type: e.target.value })}
                className="w-full bg-[#000B18] border border-white/20 text-white rounded-md px-3 py-2 text-sm"
                data-testid={`${testIdPrefix}-svc-type`}>
                <option value="service">Service</option>
                <option value="product">Product</option>
              </select>
            </div>
            <div>
              <Label className="text-[#A0B4CC] text-xs">Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className="bg-[#000B18] border-white/20 text-white" data-testid={`${testIdPrefix}-svc-name`} />
            </div>
            <div>
              <Label className="text-[#A0B4CC] text-xs">Description *</Label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                rows={3} className="w-full px-3 py-2 rounded-md bg-[#000B18] border border-white/20 text-white text-sm focus:border-[#00E5FF] focus:outline-none resize-none"
                data-testid={`${testIdPrefix}-svc-desc`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#A0B4CC] text-xs">Regular Price (PHP) *</Label>
                <Input type="number" min="0" step="0.01" value={form.regular_price}
                  onChange={e => setForm({ ...form, regular_price: e.target.value })}
                  className="bg-[#000B18] border-white/20 text-white"
                  data-testid={`${testIdPrefix}-svc-price`} />
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Category *</Label>
                <CategoryCombobox
                  api={api}
                  itemType={form.item_type || 'service'}
                  value={form.category_id}
                  onChange={(catId, catName) => setForm({
                    ...form,
                    category_id: catId,
                    category: catName || form.category,
                  })}
                  placeholder="Pick or search a category…"
                  testId={`${testIdPrefix}-svc-category`}
                />
                {form.category && !form.category_id && (
                  <p className="text-[#FFA726] text-[10px] mt-1">
                    Legacy category &quot;{form.category}&quot; — please pick from the list so customers can find this in search/filters.
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label className="text-[#A0B4CC] text-xs">Tags (comma-separated)</Label>
              <Input value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })}
                placeholder="discount, weekly, vegan"
                className="bg-[#000B18] border-white/20 text-white" />
            </div>

            {/* Tier-based Pricing (Discounted Price per Plan) */}
            {plans.length > 0 && (() => {
              // Live tier-pricing rules check (mirrors backend). The lowest
              // tier with a set price must be ≥10% off; each higher tier
              // must give a strictly higher discount %.
              const rp = parseFloat(form.regular_price) || 0;
              const setTiers = plans
                .map(p => ({ ...p, tp: parseFloat(form.tier_prices?.[p.id] || 0) }))
                .filter(p => p.tp > 0);
              let tierError = '';
              if (setTiers.length > 0 && rp > 0) {
                let prevDisc = null;
                for (let i = 0; i < setTiers.length; i++) {
                  const sp = setTiers[i];
                  const d = ((rp - sp.tp) / rp) * 100;
                  if (i === 0 && d < 10) {
                    tierError = `${sp.name} must be at least 10% off (currently ${d.toFixed(1)}%).`;
                    break;
                  }
                  if (i > 0 && d <= prevDisc) {
                    tierError = `${sp.name} discount (${d.toFixed(1)}%) must be higher than ${setTiers[i - 1].name} (${prevDisc.toFixed(1)}%).`;
                    break;
                  }
                  prevDisc = d;
                }
              }
              return (
                <div className="space-y-2" data-testid={`${testIdPrefix}-tier-pricing-block`}>
                  <Label className="text-white text-sm">Tier Pricing (Discounted Price per Plan)</Label>
                  <p className="text-[#5C7599] text-[10px]">Pro must be at least 10% off; Elite must give a deeper discount than Pro.</p>
                  <div className="space-y-2">
                    {plans.map(plan => {
                      const tierVal = form.tier_prices?.[plan.id] || '';
                      const tp = parseFloat(tierVal) || 0;
                      const disc = rp > 0 && tp > 0 && tp < rp ? Math.round(((rp - tp) / rp) * 100) : 0;
                      return (
                        <div key={plan.id} className="flex items-center gap-2">
                          <div className="w-24 flex-shrink-0">
                            <span className="text-[#A0B4CC] text-xs font-medium">{plan.name}</span>
                          </div>
                          <Input
                            type="number" min="0" step="0.01"
                            value={tierVal}
                            onChange={e => setForm({ ...form, tier_prices: { ...form.tier_prices, [plan.id]: e.target.value } })}
                            placeholder={form.regular_price || '0.00'}
                            className="bg-[#000B18] border-white/20 text-white h-9 text-sm flex-1"
                            data-testid={`${testIdPrefix}-tier-price-${plan.id}`}
                          />
                          {disc > 0 && <span className="text-[#00E676] text-[10px] font-medium w-12 flex-shrink-0">{disc}% off</span>}
                        </div>
                      );
                    })}
                  </div>
                  {tierError && (
                    <p className="text-[#FF1744] text-xs flex items-center gap-1" data-testid={`${testIdPrefix}-tier-error`}>
                      ⚠️ {tierError}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Image */}
            <div>
              <Label className="text-[#A0B4CC] text-xs">Image</Label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-lg bg-[#000B18] border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {imagePreview
                    ? <ImageWithFallback
                        src={imagePreview}
                        alt="Service preview"
                        className="w-full h-full object-cover"
                        fallback={<ImageIcon className="w-6 h-6 text-[#5C7599]" />}
                      />
                    : <ImageIcon className="w-6 h-6 text-[#5C7599]" />
                  }
                </div>
                <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect}
                  data-testid={`${testIdPrefix}-svc-image-input`} />
                <Button type="button" variant="outline" onClick={() => imageInputRef.current?.click()}
                  className="border-white/20 text-[#A0B4CC]" data-testid={`${testIdPrefix}-svc-image-pick`}>
                  {imagePreview ? 'Replace' : 'Upload'}
                </Button>
              </div>
              <p className="text-[10px] text-[#5C7599] mt-1">Recommended: 1200×800 · auto-compressed on upload</p>
            </div>

            {editing && (
              <label className="flex items-center gap-2 text-sm text-[#A0B4CC]">
                <input type="checkbox" checked={!!form.is_active}
                  onChange={e => setForm({ ...form, is_active: e.target.checked })}
                  data-testid={`${testIdPrefix}-svc-active`} />
                Listing is active (visible in Marketplace)
              </label>
            )}
          </div>

          <DialogFooter className="mt-3">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
            <Button onClick={handleSave} disabled={saving}
              className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90"
              data-testid={`${testIdPrefix}-save-service-btn`}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" />{editing ? 'Save' : 'Create'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="bg-[#041A3B] border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#FF1744] flex items-center gap-2">
              <Trash2 className="w-5 h-5" /> Delete service?
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#A0B4CC] text-sm">
            Permanently delete <span className="text-white font-medium">{confirmDelete?.name}</span>? This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)} className="text-[#A0B4CC]">Cancel</Button>
            <Button onClick={handleDelete}
              className="bg-[#FF1744] text-white hover:bg-[#FF1744]/90"
              data-testid={`${testIdPrefix}-confirm-delete-service-btn`}>
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
